import { Grid, Icon, Modal } from "semantic-ui-react";

import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCollectionData } from "astrid-firestore/src/hooks";

import Flex from "../../../../components/Flex/Flex";
import CoverImage from "../../../ui/components/CoverImage/CoverImage";
import Detail from "../../../ui/components/Detail/Detail";
import Menu from "../../../ui/components/Menu/Menu";
import Popup from "../../../ui/components/Popup/Popup";

import ArticleVersionsModalChannelSection from "./components/ArticleVersionsModalChannelSection";
import useArticleVersionsModalFields from "./hooks/useArticleVersionsModalFields";

export default function ArticleVersionsModal({ article, channels, onClose }) {
	const { t } = useTranslation();

	const [versions] = useCollectionData(article.ref.collection("versions").orderBy("created", "desc"));

	const [activeIndex, setActiveIndex] = useState(0);
	const [showOnlyUpdatedFields, setShowOnlyUpdatedFields] = useState(false);

	const activeVersion = versions?.[activeIndex];

	const options = versions?.map((version, index) => {
		return {
			active: index === activeIndex,
			content: (
				<>
					{version.created?.toDate().toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
					<p style={{ fontSize: 11 }}>
						{version.createdBy?.name} {index === 0 && `(${t("latestVersion", "Latest version")})`}
					</p>
				</>
			),
			onClick: () => setActiveIndex(index),
		};
	});

	const fields = useArticleVersionsModalFields(activeVersion);

	if (!activeVersion) {
		return null;
	}

	const versionChannels = (activeVersion.after.channelIds || []).map((channelId) => ({
		id: channelId,
		channel: channels?.find((c) => c.id === channelId),
	}));

	const showSubChannels = versionChannels.some(({ channel }) => channel?.subChannels?.length > 0);

	return (
		<Modal open padded size="fullscreen" onClose={onClose}>
			<Modal.Header>{t("versions", "Versions")}</Modal.Header>

			<Modal.Content>
				<Grid>
					<Grid.Column width={3}>
						<Menu vertical pointing secondary options={options} />
					</Grid.Column>

					<Grid.Column width={13}>
						<Flex alignItems="flex-start" style={{ marginBottom: "1em" }}>
							<CoverImage data={activeVersion.after} size={80} />

							<Popup
								content={t("onlyShowUpdatedFields", "Show only updated fields")}
								position="top right"
								trigger={
									<Icon
										name="eye"
										color={showOnlyUpdatedFields ? "blue" : "grey"}
										style={{ cursor: "pointer", fontSize: 16 }}
										onClick={() => setShowOnlyUpdatedFields((value) => !value)}
									/>
								}
							/>
						</Flex>

						<div
							style={{
								display: "grid",
								gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
								gap: "1em",
								marginBottom: "1em",
							}}
						>
							{fields
								.filter((field) => (showOnlyUpdatedFields ? field.highlighted : true))
								.map(({ key, label, value, href, highlighted, removed }) => (
									<Detail
										key={key}
										label={label}
										value={value || "N/A"}
										href={href}
										highlighted={highlighted}
										removed={removed}
										style={key === "synopsis" ? { gridColumn: "1 / -1" } : undefined}
									/>
								))}
						</div>

						{versionChannels.map(({ id, channel }) => (
							<ArticleVersionsModalChannelSection
								key={id}
								name={channel?.name ?? id}
								showSubChannels={showSubChannels}
								hasSubChannels={channel?.subChannels?.length > 0}
								before={activeVersion.before?.channels?.[id]}
								after={activeVersion.after.channels?.[id]}
								showOnlyUpdatedFields={showOnlyUpdatedFields}
							/>
						))}
					</Grid.Column>
				</Grid>
			</Modal.Content>
		</Modal>
	);
}
